/**
 * 표 머리 정렬 — 같은 머리를 누를 때마다 **내림차순 → 오름차순 → 기본(받은 순서)** 으로 돈다.
 *
 * 순환은 프리셋 표(PresetPage/PresetTable.js handleSort)와 같다 — 같은 제품의 표가 누를 때마다 다른
 * 차례로 돌면, 사용자는 표마다 몇 번 눌러야 원래대로 오는지를 다시 배워야 한다.
 * 다른 머리를 누르면 그 칸의 내림차순부터 다시 시작한다.
 *
 * 브라우저가 가진 행만 줄 세운다. 그래서 **목록을 통째로 받는 표에만** 붙인다 — 한 쪽씩 받는 표에
 * 붙이면 「이 쪽 안에서의 차례」를 전체 차례인 양 보여 준다(ExamPapersV2/PaperTable 머리 주석).
 */
import { parseV2Timestamp } from '../../utils/v2DateTime';

/** 다음 정렬 — null 이 「기본」이다(정렬 없음 = 서버가 준 차례 그대로) */
export function nextSort(prev, key) {
  if (prev?.key !== key) return { key, order: 'desc' };
  if (prev.order === 'desc') return { key, order: 'asc' };
  return null;
}

/** 머리 칸의 aria-sort — 정렬 중인 칸에만 단다 */
export function ariaSortOf(sort, key) {
  if (sort?.key !== key) return undefined;
  return sort.order === 'asc' ? 'ascending' : 'descending';
}

/** 값이 없는 칸 — 표가 「—」·「-」 로 그리는 칸이다 */
const isBlank = (v) => v == null || v === '' || (typeof v === 'number' && Number.isNaN(v));

// numeric — 「중1 · 중2 · 중10」, 「rev 2 · rev 10」 이 글자 차례가 아니라 수 차례로 선다
const collator = new Intl.Collator('ko', { numeric: true });

/**
 * 줄 세우기. `valueOf(row, key)` 는 칸마다 **화면에 그린 값**을 돌려준다(수 · 글자 · null).
 *
 * 값이 없는 행은 방향과 상관없이 **늘 맨 아래**다 — 내림차순에서 「—」 줄이 맨 위로 올라오면
 * 값이 있는 줄을 찾으러 매번 내려가야 한다.
 * 값이 같으면 받은 차례를 지킨다 — 같은 값끼리 누를 때마다 자리가 바뀌면 표가 흔들려 보인다.
 * 정렬이 없으면 받은 배열을 그대로 돌려준다(새로 만들지 않는다).
 */
export function sortRows(rows, sort, valueOf) {
  const list = Array.isArray(rows) ? rows : [];
  if (sort == null || sort.key == null) return list;
  const dir = sort.order === 'asc' ? 1 : -1;
  return list
    .map((row, i) => ({ row, i, v: valueOf(row, sort.key) }))
    .sort((a, b) => {
      const aBlank = isBlank(a.v);
      const bBlank = isBlank(b.v);
      if (aBlank || bBlank) {
        if (aBlank && bBlank) return a.i - b.i;
        return aBlank ? 1 : -1;
      }
      const c = typeof a.v === 'number' && typeof b.v === 'number'
        ? a.v - b.v
        : collator.compare(String(a.v), String(b.v));
      return c * dir || a.i - b.i;
    })
    .map((e) => e.row);
}

/** 수 칸의 정렬 값 — 숫자가 아니면 null(「0」과 「값이 안 옴」을 뭉개지 않는다) */
export function numSortValue(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** 글자 칸의 정렬 값 — 공백뿐이면 빈 칸이다 */
export function textSortValue(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

/**
 * 시각 칸의 정렬 값(ms). 화면이 시각을 읽는 규칙(utils/v2DateTime — 존 없는 값도 UTC)과 같은 자로 읽는다.
 * 문자열 그대로 견주면 「…Z」 와 「…+09:00」 이 섞인 응답에서 차례가 틀린다.
 */
export function timeSortValue(v) {
  const d = parseV2Timestamp(v);
  return d == null ? null : d.getTime();
}
